import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { useListZonePricingPlans } from 'src/features/zone/api/useListZonePricingPlans';
import CreateZonePricingPlanFormModal from 'src/features/zone/components/form/CreateZonePricingPlanFormModal';
import CellZonePricingPlanActions from 'src/features/zone/components/table/CellZonePricingPlanActions';
import { ZonePricingPlan } from 'src/features/zone/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { CellDatetime } from 'src/registry/components/table';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

const sortingOptions = {
    initialSortingState: [],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<ZonePricingPlan>();
type Props = {
    zoneId?: string;
};
export default function ZonePricingPlans({ zoneId }: Props) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const manageZones = useHasPermissions([PERMISSION.MANAGE_ZONES]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);

    const payload = React.useMemo(
        () => ({
            zone: zoneId,
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, zoneId, parkingId, queryPaginationOptions],
    );

    const { data, isLoading } = useListZonePricingPlans(payload);

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('translations', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                cell: ({ row }) => {
                    const name = row.original.translations?.find((item) => item.language === locale)?.name ?? '-';
                    const pricingPlanId = row.original.translations?.find(
                        (item) => item.language === locale,
                    )?.pricing_plan;

                    return (
                        name &&
                        pricingPlanId && (
                            <LinkWithIcon
                                to={`/${PATH.ADMINISTRATION}/pricing-plan/${pricingPlanId}`}
                                linkTitle={name}
                                hasPermissions={accessPricingPlans}
                            />
                        )
                    );
                },
                meta: {
                    title: formatMessage(commonMessages.name),
                },
            }),
            columnHelper.accessor('valid_from', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                cell: CellDatetime,
                meta: {
                    title: formatMessage(pricingPlanMessages.activeFrom),
                },
            }),
            columnHelper.accessor('valid_to', {
                header: DataTableColumnHeader,
                enableSorting: true,
                enableHiding: true,
                cell: CellDatetime,
                meta: {
                    title: formatMessage(pricingPlanMessages.activeTo),
                },
            }),
            ...(manageZones
                ? [
                      columnHelper.display({
                          id: 'actions',
                          header: DataTableColumnHeader,
                          cell: CellZonePricingPlanActions,
                      }),
                  ]
                : []),
        ],
        [formatMessage, locale, manageZones, accessPricingPlans],
    );

    return (
        <DataTable<ZonePricingPlan, any>
            pagination={pagination}
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            semantics={{
                title: formatMessage(pricingPlanMessages.pricingPlans),
                entryName: formatMessage(pricingPlanMessages.pricingPlan).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.pricingPlans).toLowerCase(),
                placeholder: formatMessage(pricingPlanMessages.pricingPlan),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageZones && { newEntry: CreateZonePricingPlanFormModal }),
            }}
            enableSearchParams={false}
        />
    );
}
